import * as React from "react";
import { HeaderIcon } from "../atoms/header-icon.js";
import { cx } from "../internal/cx.js";
import { HeadingGroup, type HeadingGroupHeadingLevel } from "../molecules/heading-group.js";

/**
 * SectionHeader — reusable header for page sections, cards, panels, and
 * organism-level blocks.
 *
 * Nearly every `*Section` organism starts with the same structure: eyebrow/
 * status, title, description, and an optional action. Defaults follow the V2
 * baseline: a bare muted icon inline with a `heading-medium` title, a
 * `body-small` description, and a single action aligned in the header row
 * (right, on tablet+). Pass `iconTreatment="medallion"` or
 * `actionsPlacement="footer"` for the legacy layout. Keep domain-specific copy
 * and actions in the parent; this component only owns layout and hierarchy.
 */

export type SectionHeaderDensity = "comfortable" | "compact";
export type SectionHeaderAlign = "start" | "center";
/** Where section actions sit. Header placement stacks only on mobile. */
export type SectionHeaderActionsPlacement = "header" | "footer";
/**
 * How the leading icon renders.
 *   - `bare` (default) — a muted 20px icon inline with the title (the V2 baseline).
 *   - `medallion` — the tinted `HeaderIcon` circle (legacy look).
 */
export type SectionHeaderIconTreatment = "bare" | "medallion";

export interface SectionHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Optional small label above the title ("Billing", "Step 2"). */
  eyebrow?: React.ReactNode;
  /** Main section title. */
  title: React.ReactNode;
  /** Semantic heading level. Defaults to `h2`; use `3` for nested card sections. */
  headingLevel?: HeadingGroupHeadingLevel;
  /** Supporting copy under the title. */
  description?: React.ReactNode;
  /** Small metadata/status slot next to the title. */
  meta?: React.ReactNode;
  /**
   * Action area for the section — typically a single `Button variant="ghost"
   * size="sm"` (icon-prefixed for edit). For two or more controls, prefer
   * `actionsPlacement="footer"` so they get a full-width row.
   */
  actions?: React.ReactNode;
  /**
   * `header` (default): the action aligns with the title row on tablet+, and
   * stacks below on mobile. `footer`: actions on a full-width row under the
   * title/description on all breakpoints (use for two or more controls).
   */
  actionsPlacement?: SectionHeaderActionsPlacement;
  /** Optional leading visual for dashboard or empty-state sections. */
  icon?: React.ReactNode;
  /** Icon rendering — bare muted icon (default, V2 baseline) or the medallion. */
  iconTreatment?: SectionHeaderIconTreatment;
  /** Spacing density. */
  density?: SectionHeaderDensity;
  /** Vertical alignment between text and actions. */
  align?: SectionHeaderAlign;
}

export const SectionHeader = React.forwardRef<HTMLDivElement, SectionHeaderProps>(
  function SectionHeader(
    {
      actions,
      actionsPlacement = "header",
      align = "start",
      className,
      density = "comfortable",
      description,
      eyebrow,
      headingLevel,
      icon,
      iconTreatment = "bare",
      meta,
      title,
      ...props
    },
    ref,
  ) {
    const bareIcon = iconTreatment === "bare";
    return (
      <div
        ref={ref}
        className={cx(
          "cw-v2-section-header",
          `cw-v2-section-header--${density}`,
          `cw-v2-section-header--align-${align}`,
          icon ? "cw-v2-section-header--has-icon" : null,
          icon && bareIcon ? "cw-v2-section-header--icon-bare" : null,
          actions ? "cw-v2-section-header--has-actions" : null,
          actions ? `cw-v2-section-header--actions-${actionsPlacement}` : null,
          className,
        )}
        {...props}
      >
        {icon ? (
          bareIcon ? (
            <span
              aria-hidden
              className="cw-v2-section-header__icon cw-v2-section-header__icon--bare"
            >
              {icon}
            </span>
          ) : (
            <HeaderIcon className="cw-v2-section-header__icon" icon={icon} />
          )
        ) : null}
        <HeadingGroup
          className="cw-v2-section-header__content"
          description={description}
          descriptionVariant="body-small"
          eyebrow={eyebrow}
          headingLevel={headingLevel}
          meta={meta}
          title={title}
          titleVariant="heading-medium"
          variant="section"
        />
        {actions ? <div className="cw-v2-section-header__actions">{actions}</div> : null}
      </div>
    );
  },
);
SectionHeader.displayName = "SectionHeader";
